import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Tag,
    Spinner,
} from '@chakra-ui/react';
import { useGetOrderDetail, SupplierOrderStatus } from '../../hooks/useGetOrderDetail';
import { OrderChatPanel } from '../../modals/OrderChatDrawer';
import SupplierOrderChatPanel from './SupplierOrderChatPanel';
import SupplierOrderSnapshotModal from '../../modals/SupplierOrderSnapshotModal';
import SourceBadge from '../SourceBadge';
import SupplierOrderInfoPopover from '../SupplierOrderInfoPopover';
import { Badge, Button, Icon, Tooltip } from '../../ui';

const getStatusColor = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed': return 'purple';
        case 'ordered': return 'teal';
        case 'rejected': return 'gray';
        case 'cancelled': return 'red';
        default: return 'gray';
    }
};

const getStatusLabel = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed': return 'Confirmed';
        case 'ordered': return 'Ordered';
        case 'rejected': return 'Rejected';
        case 'cancelled': return 'Cancelled';
        default: return status;
    }
};

export interface ChatDetailSelection {
    channel: 'internal' | 'vendor';
    orderId: string;
    supplierOrderId?: string;
    vendorName?: string;
}

interface ChatDetailPaneProps {
    selection: ChatDetailSelection | null;
    onChatRead?: (selection: ChatDetailSelection) => void;
}

const ChatDetailPane = ({ selection, onChatRead }: ChatDetailPaneProps) => {
    const [isSnapshotOpen, setIsSnapshotOpen] = useState(false);

    const { data: orderDetail, isLoading, refetch } = useGetOrderDetail(
        selection?.orderId ?? '',
        { enabled: !!selection?.orderId },
    );

    useEffect(() => {
        if (selection) onChatRead?.(selection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection?.channel, selection?.orderId, selection?.supplierOrderId]);

    const supplierSection = useMemo(() => {
        if (!selection?.supplierOrderId || !orderDetail) return undefined;
        for (const tab of orderDetail.ordered) {
            const match = tab.sections.find(s => s.key === selection.supplierOrderId);
            if (match) return match;
        }
        return undefined;
    }, [orderDetail, selection?.supplierOrderId]);

    if (!selection) {
        return (
            <Flex flex={1} align="center" justify="center" h="100%" bg="bg.subtle">
                <VStack spacing={3}>
                    <Flex
                        align="center"
                        justify="center"
                        w="56px"
                        h="56px"
                        borderRadius="full"
                        bg="bg.surface"
                        borderWidth="1px"
                        borderColor="border.default"
                    >
                        <Icon name="message" size="lg" color="brand.solid" />
                    </Flex>
                    <VStack spacing={0.5}>
                        <Text fontSize="sm" fontWeight="semibold" color="fg.default">No conversation selected</Text>
                        <Text fontSize="xs" color="fg.muted">Choose a thread on the left to view messages</Text>
                    </VStack>
                </VStack>
            </Flex>
        );
    }

    const isVendor = selection.channel === 'vendor';
    const ymm = orderDetail?.vehicleName ?? '';
    const ro = orderDetail?.roNumber ?? '';
    const vendorName = supplierSection?.title ?? selection.vendorName ?? 'Vendor';
    const placedAt = supplierSection?.timePlaced
        ? `${new Date(supplierSection.timePlaced).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • ${new Date(supplierSection.timePlaced).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
        : '';

    return (
        <Box h="100%" display="flex" flexDirection="column" bg="bg.surface" minH={0}>
            <Box px={5} py={3} borderBottomWidth="1px" borderColor="border.subtle" flexShrink={0}>
                <Flex justify="space-between" align="center" gap={3}>
                    <HStack spacing={3} align="center" minW={0} flexWrap="wrap">
                        {isVendor && (
                            <>
                                <Text fontSize="md" fontWeight="semibold" color="fg.default" noOfLines={1}>
                                    {vendorName}
                                </Text>
                                <SourceBadge source={supplierSection?.source ?? 'partstrader'} />
                                {supplierSection?.displayId && (
                                    <Text
                                        fontSize="xs" color="fg.muted" fontWeight="medium" bg="bg.subtle"
                                        border="1px solid" borderColor="border.default" borderRadius="md" px={2} py={0.5} lineHeight="1.2"
                                    >
                                        {supplierSection.displayId}
                                    </Text>
                                )}
                            </>
                        )}
                        <Tooltip label="Open order page" placement="top" openDelay={300}>
                            <Text
                                as="button"
                                fontSize={isVendor ? 'sm' : 'md'}
                                fontWeight={isVendor ? 'medium' : 'bold'}
                                color="brand.solid"
                                onClick={() => window.open(`/order/${selection.orderId}`, '_blank')}
                                _hover={{ textDecoration: 'underline' }}
                            >
                                {ymm || 'View order'}
                            </Text>
                        </Tooltip>
                        {ro && <Text fontSize="sm" color="fg.muted">RO {ro}</Text>}
                        {!isVendor && orderDetail?.archived && (
                            <Badge colorScheme="orange" fontSize="11px">Archived</Badge>
                        )}
                        {!isVendor && !orderDetail?.archived && orderDetail?.completed && (
                            <Badge colorScheme="green" fontSize="11px">Completed</Badge>
                        )}
                    </HStack>

                    <HStack spacing={3} flexShrink={0} align="center">
                        {isVendor && supplierSection?.status && (
                            <HStack spacing={2} align="center">
                                <Tag size="sm" colorScheme={getStatusColor(supplierSection.status)} variant="subtle" px={1.5} fontSize="11px">
                                    {getStatusLabel(supplierSection.status)}
                                </Tag>
                                {placedAt && (
                                    <>
                                        <Text fontSize="sm" color="fg.muted" fontWeight="bold">•</Text>
                                        <Text fontSize="sm" color="fg.muted" fontWeight="semibold" whiteSpace="nowrap">{placedAt}</Text>
                                    </>
                                )}
                            </HStack>
                        )}
                        {isVendor && (
                            <SupplierOrderInfoPopover
                                label="Contact Info"
                                phone={supplierSection?.phoneNumber}
                                email={supplierSection?.email}
                            />
                        )}
                        <Button
                            size="xs"
                            variant="ghostGray"
                            leftIcon={<Icon name="externalLink" size="sm" />}
                            onClick={() => {
                                if (isVendor && supplierSection) setIsSnapshotOpen(true);
                                else window.open(`/order/${selection.orderId}`, '_blank');
                            }}
                        >
                            {isVendor ? 'View Purchase Order' : 'View Order'}
                        </Button>
                    </HStack>
                </Flex>
            </Box>

            <Box flex={1} minH={0}>
                {isLoading && !orderDetail ? (
                    <Flex h="100%" align="center" justify="center"><Spinner color="brand.solid" /></Flex>
                ) : isVendor && selection.supplierOrderId ? (
                    <SupplierOrderChatPanel supplierOrderId={selection.supplierOrderId} supplierName={vendorName} />
                ) : (
                    <OrderChatPanel
                        orderId={selection.orderId}
                        mode="order"
                        notes={orderDetail?.notes ?? []}
                        mentionNames={orderDetail?.mentionNames}
                        refetch={refetch}
                        embedded
                        hideHeader
                        threadBg="bg.subtle"
                    />
                )}
            </Box>

            <SupplierOrderSnapshotModal
                isOpen={isSnapshotOpen}
                onClose={() => setIsSnapshotOpen(false)}
                section={supplierSection}
                vin={orderDetail?.vin ?? ''}
                roNumber={ro}
                ymm={ymm}
            />
        </Box>
    );
};

export default ChatDetailPane;
